// src/components/pages/ViewCart.jsx
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaArrowLeft, 
  FaTrash, 
  FaMinus, 
  FaPlus, 
  FaShoppingCart,
  FaCreditCard,
  FaRupeeSign,
  FaCheck,
  FaSpinner,
  FaTimes,
  FaUtensils,
  FaLeaf,
  FaFish
} from 'react-icons/fa';
import { getColors } from '../../styles/colors';
import {
  fetchCartItemsAsync,
  removeFromCartAsync,
  updateCartAsync,
  clearError
} from '../../redux/slices/cartSlice';

const ViewCart = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useSelector((state) => state.theme?.mode || 'light');
  const isDark = theme === 'dark';
  const colors = getColors(isDark);

  // Get cart state from Redux
  const {
    items: cartItems,
    totalItems,
    totalPrice,
    isLoading,
    error
  } = useSelector((state) => state.cart || {
    items: [],
    totalItems: 0,
    totalPrice: 0,
    isLoading: false,
    error: null
  });

  const [isUpdating, setIsUpdating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [removingItem, setRemovingItem] = useState(null);

  // Fetch cart items on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      dispatch(fetchCartItemsAsync());
    } else {
      navigate('/login');
    }
  }, [dispatch, navigate]);

  // ✅ POST - Update quantity in cart
  const handleUpdateQuantity = async (cartId, newQuantity) => {
    if (newQuantity < 1) return;
    setIsUpdating(true);
    try {
      await dispatch(updateCartAsync({
        items: [{ item_id: cartId, quantity: newQuantity }]
      })).unwrap();
      
      await dispatch(fetchCartItemsAsync());
      
    } catch (err) {
      console.error('❌ Failed to update quantity:', err);
      alert('Failed to update quantity. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // ✅ DELETE - Remove from cart
  const handleRemoveItem = async (cartId) => {
    if (!window.confirm('Remove this item from cart?')) return;
    setRemovingItem(cartId);
    try {
      await dispatch(removeFromCartAsync(cartId)).unwrap();
      await dispatch(fetchCartItemsAsync());
      
      setSuccessMessage('🗑️ Item removed from cart');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      
    } catch (err) {
      console.error('❌ Failed to remove item:', err);
      alert('Failed to remove item. Please try again.');
    } finally {
      setRemovingItem(null);
    }
  };

  // Calculate totals
  const calculateTotal = () => {
    return cartItems.reduce((sum, item) => sum + (parseFloat(item.price) * parseInt(item.quantity)), 0);
  };

  const cartTotal = totalPrice || calculateTotal();
  const gst = cartTotal * 0.18;
  const deliveryFee = cartTotal > 0 ? 0 : 0;
  const grandTotal = cartTotal + gst + deliveryFee;

  // Clear error
  const handleClearError = () => {
    dispatch(clearError());
  };

  // Navigate back
  const handleBack = () => {
    navigate('/cart-service');
  };

  // Proceed to checkout
  const handleCheckout = () => {
    if (cartItems.length === 0) {
      alert('Your cart is empty. Please add items before checkout.');
      return;
    }
    navigate('/checkout-service');
  };

  // Get item type icon
  const getTypeIcon = (item) => {
    const type = item.item_type || item.type || 'product';
    if (type === 'Veg' || type === 'veg') {
      return <FaLeaf className="text-green-500" />;
    } else if (type === 'Non-Veg' || type === 'nonveg' || type === 'NonVeg') {
      return <FaFish className="text-red-500" />;
    }
    return <FaUtensils className="text-yellow-500" />;
  };

  // Get item display name
  const getItemName = (item) => {
    return item.product_name || item.name || `Item ${item.item_id || item.id}`;
  };

  // Get item description
  const getItemDescription = (item) => {
    if (item.in_thali) {
      const items = item.in_thali.split('\n').filter(i => i.trim());
      return items.slice(0, 3).map(i => i.trim());
    }
    return [];
  };

  if (isLoading && cartItems.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" 
           style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 rounded-full animate-spin mx-auto"
               style={{ 
                 borderColor: colors.primary,
                 borderTopColor: 'transparent'
               }}></div>
          <p className="mt-4 font-medium" style={{ color: colors.textSecondary }}>
            Loading your cart...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Success Toast */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-xl shadow-lg flex items-center gap-3"
            style={{ 
              backgroundColor: '#22C55E',
              color: 'white'
            }}
          >
            <FaCheck />
            {successMessage}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="relative pt-8 pb-6 px-4"
           style={{
             background: `linear-gradient(180deg, ${colors.background} 0%, ${colors.background} 100%)`
           }}>
        <div className="container mx-auto">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 mb-4 px-4 py-2 rounded-full transition-all hover:scale-105"
            style={{ 
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`,
              color: colors.text
            }}
          >
            <FaArrowLeft />
            <span>Continue Shopping</span>
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold" style={{ color: colors.text }}>
                <FaShoppingCart className="inline mr-3" style={{ color: colors.primary }} />
                My Cart
              </h1>
              <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                {cartItems.length} items in your cart
              </p>
            </div>
            {cartItems.length > 0 && (
              <span className="px-4 py-2 rounded-full text-sm font-semibold"
                    style={{ 
                      backgroundColor: colors.primary + '20',
                      color: colors.primary
                    }}>
                {cartItems.length} Items
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 p-3 rounded-lg text-sm flex items-start gap-2"
               style={{ 
                 backgroundColor: '#FEF2F2',
                 color: '#DC2626',
                 border: '1px solid #FECACA'
               }}>
            <span className="mt-0.5">⚠️</span>
            <span>{error}</span>
            <button
              onClick={handleClearError}
              className="ml-auto text-xs font-medium hover:underline"
              style={{ color: '#DC2626' }}
            >
              Dismiss
            </button>
          </div>
        )}

        {cartItems.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🛒</div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: colors.text }}>
              Your cart is empty
            </h2>
            <p className="mb-6" style={{ color: colors.textSecondary }}>
              Looks like you haven't added any items to your cart yet.
            </p>
            <button
              onClick={() => navigate('/cart-service')}
              className="px-6 py-3 rounded-full text-white font-medium transition-all hover:scale-105"
              style={{ backgroundColor: colors.primary }}
            >
              Browse Products
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cartItems.map((item, index) => {
                const itemName = getItemName(item);
                const itemPrice = parseFloat(item.price) || 0;
                const itemQuantity = parseInt(item.quantity) || 1;
                const cartId = item.cart_id || item.id;
                const descriptionItems = getItemDescription(item);
                const isRemoving = removingItem === cartId;
                
                return (
                  <motion.div
                    key={cartId || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="rounded-xl p-4 transition-all hover:shadow-lg"
                    style={{ 
                      backgroundColor: colors.card,
                      border: `1px solid ${colors.border}`
                    }}
                  >
                    <div className="flex flex-col sm:flex-row gap-4">
                      {/* Product Image */}
                      <div className="w-full sm:w-32 h-32 rounded-lg overflow-hidden flex-shrink-0 bg-gray-200">
                        <img
                          src={item.prod_image ? `https://admin.thefoodies.in/public/uploads/products/${item.prod_image}` : '/placeholder-image.jpg'}
                          alt={itemName}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = '/placeholder-image.jpg';
                          }}
                        />
                      </div>
                      
                      {/* Product Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              {getTypeIcon(item)}
                              <h3 className="font-bold text-lg" style={{ color: colors.text }}>
                                {itemName}
                              </h3>
                            </div>
                            {descriptionItems.length > 0 && (
                              <div className="mt-1 text-sm" style={{ color: colors.textSecondary }}>
                                {descriptionItems.map((desc, i) => (
                                  <div key={i} className="truncate">• {desc}</div>
                                ))}
                                {descriptionItems.length > 3 && (
                                  <span>+{descriptionItems.length - 3} more</span>
                                )}
                              </div>
                            )}
                          </div>
                          <p className="font-bold text-lg flex-shrink-0 ml-2" style={{ color: colors.primary }}>
                            ₹{(itemPrice * itemQuantity).toFixed(2)}
                          </p>
                        </div>
                        
                        {/* ✅ Updated Quantity Controls with Better Design */}
                        <div className="flex items-center justify-between mt-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleUpdateQuantity(cartId, itemQuantity - 1)}
                              disabled={isUpdating || isRemoving}
                              className="w-9 h-9 rounded-full flex items-center justify-center text-white transition-all hover:scale-105 disabled:opacity-50"
                              style={{ backgroundColor: '#EF4444' }}
                            >
                              <FaMinus className="text-xs" />
                            </button>
                            <span className="font-semibold min-w-[32px] text-center text-base" style={{ color: colors.text }}>
                              {itemQuantity}
                            </span>
                            <button
                              onClick={() => handleUpdateQuantity(cartId, itemQuantity + 1)}
                              disabled={isUpdating || isRemoving}
                              className="w-9 h-9 rounded-full flex items-center justify-center text-white transition-all hover:scale-105 disabled:opacity-50"
                              style={{ backgroundColor: '#22C55E' }}
                            >
                              <FaPlus className="text-xs" />
                            </button>
                          </div>
                          
                          <button
                            onClick={() => handleRemoveItem(cartId)}
                            disabled={isUpdating || isRemoving}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:scale-105 disabled:opacity-50"
                            style={{ 
                              backgroundColor: '#FEE2E2',
                              color: '#DC2626'
                            }}
                          >
                            {isRemoving ? (
                              <FaSpinner className="animate-spin" />
                            ) : (
                              <FaTrash className="text-xs" />
                            )}
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Price per item */}
                    <div className="mt-2 pt-2 border-t text-sm" style={{ borderColor: colors.border, color: colors.textSecondary }}>
                      ₹{itemPrice.toFixed(2)} × {itemQuantity} = <span className="font-semibold" style={{ color: colors.primary }}>₹{(itemPrice * itemQuantity).toFixed(2)}</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Right Column - Order Summary */}
            <div className="lg:col-span-1">
              <div className="rounded-xl p-6 sticky top-4"
                   style={{ 
                     backgroundColor: colors.card,
                     border: `1px solid ${colors.border}`
                   }}>
                <h3 className="text-lg font-bold mb-4" style={{ color: colors.text }}>
                  Cart Summary
                </h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-sm" style={{ color: colors.textSecondary }}>
                    <span>Items ({cartItems.length})</span>
                    <span>₹{cartTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm" style={{ color: colors.textSecondary }}>
                    <span>GST (18%)</span>
                    <span>₹{gst.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm" style={{ color: colors.textSecondary }}>
                    <span>Delivery Fee</span>
                    <span className="text-green-500">Free</span>
                  </div>
                  <div className="border-t pt-3 mt-3" style={{ borderColor: colors.border }}>
                    <div className="flex justify-between">
                      <span className="font-bold text-lg" style={{ color: colors.text }}>Total</span>
                      <span className="text-2xl font-bold" style={{ color: colors.primary }}>
                        ₹{grandTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3 mt-6">
                  <button
                    onClick={handleCheckout}
                    className="w-full py-3.5 rounded-xl font-semibold text-white transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
                    style={{ 
                      backgroundColor: colors.primary,
                      boxShadow: `0 4px 20px ${colors.primary}40`
                    }}
                  >
                    <FaCreditCard />
                    Proceed to Checkout
                  </button>
                  
                  <button
                    onClick={handleBack}
                    className="w-full py-3 rounded-xl font-medium transition-all hover:scale-[1.02]"
                    style={{ 
                      backgroundColor: colors.inputBackground,
                      color: colors.text,
                      border: `1px solid ${colors.border}`
                    }}
                  >
                    Continue Shopping
                  </button>
                </div>

                <div className="flex items-center justify-center gap-2 mt-4 text-xs" style={{ color: colors.textSecondary }}>
                  <FaShoppingCart className="text-green-500" />
                  <span>Secure checkout • Free delivery</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewCart;